import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Star, Phone, Flame, TrendingUp, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TackLogo } from "@/components/TackLogo";
import { PosterCard } from "@/components/PosterCard";
import { useIsMobile } from "@/hooks/use-mobile";
import { getCurrentLocale, type Locale } from "@/lib/i18n";

// Demo merchant — must match the seeded row in the migration.
const DEMO_SLUG = "demo-cafe";
const DEMO_TOKEN = "demo01";

export const Route = createFileRoute("/demo")({
  head: () => ({
    meta: [
      { title: "Try the Tack Demo. See it in action" },
      {
        name: "description",
        content:
          "Scan the live QR code with your phone and watch the dashboard light up in real time. Experience the full Tack flow in 60 seconds.",
      },
      { property: "og:title", content: "Try the Tack Demo" },
      {
        property: "og:description",
        content:
          "Scan, rate, and watch the merchant dashboard update live. The full Tack experience in 60 seconds.",
      },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/demo" }],
  }),
  component: DemoPage,
});

type FeedbackRow = {
  id: string;
  star_rating: number;
  customer_email: string;
  promo_code: string;
  status: string;
  created_at: string;
  redeemed_started_at?: string | null;
};

type BurnEntry = {
  code: string;
  email: string;
  stars: number;
  comment?: string | null;
  agoLabel: string;
  isYou?: boolean;
};

type ActiveBurn = {
  code: string;
  email: string;
  stars: number;
  comment?: string | null;
  startedAt: number;
  durationSec: number;
  isYou?: boolean;
};

// First-name pool used only to derive masked email initials.
const FAKE_NAMES = [
  "john", "sara", "mike", "anna", "elle", "luka", "emma", "noah", "olivia",
  "liam", "maya", "felix", "lina", "oscar", "iris", "viktor", "alma", "hugo",
  "freja", "nora", "axel", "leo", "saga", "ida",
];

const FAKE_DOMAINS = ["gmail.com", "hotmail.com", "yahoo.com", "outlook.com", "icloud.com"];

// Salon / beauty-industry comments. Two locale pools.
const SALON_COMMENTS_EN: Array<string | null> = [
  "Best balayage I've had in ages",
  "Loved the head massage",
  "My blow-dry lasted all week",
  "Nails look amazing ✨",
  null,
  "Brows on point",
  "Lashes are stunning",
  "Great cut, super happy",
  null,
  "Cosy atmosphere, kind staff",
  "Colour came out exactly as I wanted",
  null,
  "Best manicure in town",
  "Fringe trim was perfect",
  "A bit of a wait at the desk",
  "Booking was easy, service quick",
  null,
];

const SALON_COMMENTS_SV: Array<string | null> = [
  "Bästa balayagen på länge",
  "Underbar hårbottenmassage",
  "Föningen höll hela veckan",
  "Nagelvård i toppklass",
  null,
  "Bryn på topp",
  "Fransförlängningen sitter perfekt",
  "Superfin klippning, jättenöjd",
  null,
  "Mysig känsla och trevlig personal",
  "Färgen blev precis som jag ville",
  null,
  "Bästa manikyren i stan",
  "Lugg­klippning på pricken",
  "Bokningen tog lite tid",
  "Snabb och smidig behandling",
  null,
];

const LOW_STAR_EN = ["A bit of a wait at the desk", "Music was too loud", "Pricey but nice", null];
const LOW_STAR_SV = ["Bokningen tog lite tid", "Musiken var lite hög", "Lite dyrt men fint", null];

const PROMO_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
function randomCode(): string {
  let out = "";
  for (let i = 0; i < 4; i++) {
    out += PROMO_ALPHABET[Math.floor(Math.random() * PROMO_ALPHABET.length)];
  }
  return out;
}

function randomStars(): number {
  const r = Math.random();
  if (r < 0.55) return 5;
  if (r < 0.8) return 4;
  if (r < 0.95) return 3;
  return 2;
}

function randomMaskedPhone(): string {
  // Swedish mobile format, mid digits masked. e.g. "+46 70 *** ** 42"
  const prefix = ["70", "72", "73", "76", "79"][Math.floor(Math.random() * 5)];
  const last = String(Math.floor(Math.random() * 90) + 10);
  return `+46 ${prefix} *** ** ${last}`;
}

function randomFakeBurn(locale: Locale): Omit<BurnEntry, "agoLabel"> {
  const stars = randomStars();
  const pool = locale === "sv" ? SALON_COMMENTS_SV : SALON_COMMENTS_EN;
  const low = locale === "sv" ? LOW_STAR_SV : LOW_STAR_EN;
  const comment =
    stars <= 3 && Math.random() < 0.6
      ? low[Math.floor(Math.random() * low.length)]
      : pool[Math.floor(Math.random() * pool.length)];
  return {
    code: randomCode(),
    email: randomMaskedPhone(),
    stars,
    comment,
  };
}

function buildSeedHistory(locale: Locale, t: (k: string, o?: any) => string): BurnEntry[] {
  const mins = [2, 14, 31, 47, 60, 60];
  return mins.map((m) => ({
    ...randomFakeBurn(locale),
    agoLabel: m >= 60 ? t("demo.hAgo", { count: Math.floor(m / 60) }) : t("demo.minAgo", { count: m }),
  }));
}

function maskEmail(_email: string): string {
  // Demo shows masked phone numbers instead of emails.
  return randomMaskedPhone();
}


function PosterScaled({
  scale,
  primaryColor,
  feedbackUrl,
  topText,
  bottomText,
  merchantName,
}: {
  scale: number;
  primaryColor: string;
  feedbackUrl: string;
  topText: string;
  bottomText: string;
  merchantName?: string;
}) {
  return (
    <div style={{ width: 595 * scale, height: 842 * scale, position: "relative" }}>
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          width: 595,
          height: 842,
        }}
      >
        <PosterCard
          primaryColor={primaryColor}
          feedbackUrl={feedbackUrl}
          topText={topText}
          bottomText={bottomText}
          merchantName={merchantName}
        />
      </div>
    </div>
  );
}

function SparkleBurst({ burstKey }: { burstKey: number }) {
  if (burstKey === 0) return null;
  const particles = Array.from({ length: 10 }, (_, i) => {
    const angle = (i / 10) * Math.PI * 2;
    const distance = 28 + (i % 3) * 8;
    return {
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
      rot: (i * 47) % 360,
      delay: (i % 5) * 20,
      color:
        i % 3 === 0
          ? "bg-tack-green"
          : i % 3 === 1
            ? "bg-tack-amber"
            : "bg-primary",
    };
  });
  return (
    <div
      key={burstKey}
      className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none w-0 h-0"
      aria-hidden="true"
    >
      {particles.map((p, i) => (
        <span
          key={i}
          className={`sparkle-fly absolute block w-1.5 h-1.5 rounded-full ${p.color}`}
          style={
            {
              "--spark-x": `${p.x}px`,
              "--spark-y": `${p.y}px`,
              "--spark-rot": `${p.rot}deg`,
              animationDelay: `${p.delay}ms`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

function DemoPage() {
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const locale: Locale = (i18n.language?.startsWith("sv") ? "sv" : "en") as Locale;

  const [mounted, setMounted] = useState(false);
  const [sessionId, setSessionId] = useState<string>("");
  const [scanUrl, setScanUrl] = useState<string>("");

  useEffect(() => {
    const existing = sessionStorage.getItem("tack_demo_session");
    const id = existing ?? crypto.randomUUID();
    if (!existing) sessionStorage.setItem("tack_demo_session", id);
    setSessionId(id);
    const lang = getCurrentLocale();
    setScanUrl(
      `${window.location.origin}/tack/${DEMO_SLUG}-${DEMO_TOKEN}?session=${id}&lang=${lang}`
    );
    setMounted(true);
  }, [i18n.language]);

  const [feedback, setFeedback] = useState<FeedbackRow | null>(null);
  const [activeBurn, setActiveBurn] = useState<ActiveBurn | null>(null);
  const [recentBurns, setRecentBurns] = useState<BurnEntry[]>([]);
  const [tick, setTick] = useState(0);
  const [statFlash, setStatFlash] = useState(false);
  const [sparkleKey, setSparkleKey] = useState(0);
  const [displayRoi, setDisplayRoi] = useState(0);
  const fakeIndexRef = useRef(0);
  const prevTotalRef = useRef(0);
  const roiAnimRef = useRef<number | null>(null);

  // Seed history client-side after mount, and re-seed when locale changes
  useEffect(() => {
    setRecentBurns(buildSeedHistory(locale, t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const [posterMeta, setPosterMeta] = useState<{
    primaryColor: string;
    businessName: string;
  }>({
    primaryColor: "#D47B5E",
    businessName: "Demo Salon",
  });

  useEffect(() => {
    supabase
      .from("merchants")
      .select("brand_primary_color, business_name")
      .eq("slug", DEMO_SLUG)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        setPosterMeta({
          primaryColor: data.brand_primary_color || "#D47B5E",
          businessName: data.business_name || "Demo Salon",
        });
      });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !sessionId) return;

    const channel = supabase
      .channel(`demo-${sessionId}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "feedback_logs",
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const row = (payload.new as FeedbackRow) || null;
          if (!row) return;
          setFeedback(row);

          if (row.status === "redeemed") {
            setActiveBurn(null);
            setRecentBurns((prev) => [
              {
                code: row.promo_code,
                email: maskEmail(row.customer_email),
                stars: row.star_rating,
                agoLabel: t("demo.justNowYou"),
                isYou: true,
              },
              ...prev,
            ]);
          } else if (row.status === "redeeming" || row.redeemed_started_at) {
            const startMs = row.redeemed_started_at
              ? new Date(row.redeemed_started_at).getTime()
              : Date.now();
            setActiveBurn({
              code: row.promo_code,
              email: maskEmail(row.customer_email),
              stars: row.star_rating,
              startedAt: startMs,
              durationSec: 300,
              isYou: true,
            });
          } else {
            setActiveBurn({
              code: row.promo_code,
              email: maskEmail(row.customer_email),
              stars: row.star_rating,
              startedAt: Date.now(),
              durationSec: 300,
              isYou: true,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, t]);

  useEffect(() => {
    if (!mounted) return;
    const iv = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(iv);
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    let cancelled = false;

    const spawnFake = () => {
      if (cancelled) return;
      setActiveBurn((prev) => {
        if (prev?.isYou) return prev;
        const seed = randomFakeBurn(locale);
        fakeIndexRef.current += 1;
        return {
          code: seed.code,
          email: seed.email,
          stars: seed.stars,
          comment: seed.comment,
          startedAt: Date.now(),
          durationSec: 30,
        };
      });
    };

    const initial = setTimeout(spawnFake, 4000);
    const interval = setInterval(spawnFake, 40000);

    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, [mounted, locale]);

  useEffect(() => {
    if (!activeBurn) return;
    const elapsed = (Date.now() - activeBurn.startedAt) / 1000;
    if (elapsed >= activeBurn.durationSec && !activeBurn.isYou) {
      const expired = activeBurn;
      setActiveBurn(null);
      setRecentBurns((prev) => [
        {
          code: expired.code,
          email: expired.email,
          stars: expired.stars,
          comment: expired.comment,
          agoLabel: t("demo.justNow"),
        },
        ...prev,
      ]);
      setSparkleKey((k) => k + 1);
    }
  }, [tick, activeBurn, t]);

  const remainingSec = activeBurn
    ? Math.max(0, activeBurn.durationSec - Math.floor((Date.now() - activeBurn.startedAt) / 1000))
    : 0;
  const mins = Math.floor(remainingSec / 60);
  const secs = String(remainingSec % 60).padStart(2, "0");

  const totalToday = recentBurns.length + (activeBurn ? 1 : 0);
  const avgRating =
    recentBurns.length > 0
      ? (recentBurns.reduce((s, b) => s + b.stars, 0) / recentBurns.length).toFixed(1)
      : "—";

  useEffect(() => {
    if (totalToday > prevTotalRef.current) {
      setStatFlash(true);
      const t2 = setTimeout(() => setStatFlash(false), 800);
      prevTotalRef.current = totalToday;
      return () => clearTimeout(t2);
    }
    prevTotalRef.current = totalToday;
  }, [totalToday]);

  const targetRoi = totalToday * 75;
  useEffect(() => {
    if (typeof window === "undefined") return;
    const start = displayRoi;
    const delta = targetRoi - start;
    if (delta === 0) return;
    const duration = 600;
    const startTs = performance.now();
    if (roiAnimRef.current) cancelAnimationFrame(roiAnimRef.current);
    const step = (now: number) => {
      const elapsed = now - startTs;
      const t2 = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t2, 3);
      setDisplayRoi(Math.round(start + delta * eased));
      if (t2 < 1) roiAnimRef.current = requestAnimationFrame(step);
    };
    roiAnimRef.current = requestAnimationFrame(step);
    return () => {
      if (roiAnimRef.current) cancelAnimationFrame(roiAnimRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRoi]);

  const posterTop = t("demo.posterTop");
  const posterBottom = t("demo.posterBottom");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <TackLogo size="md" showSlogan />
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft size={14} /> {t("demo.backHome")}
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
          <div className="flex flex-col items-center">
            <div className="md:hidden">
              <PosterScaled
                scale={0.5}
                primaryColor={posterMeta.primaryColor}
                feedbackUrl={scanUrl}
                topText={posterTop}
                bottomText={posterBottom}
                merchantName={posterMeta.businessName}
              />
            </div>
            <div className="hidden md:block">
              <PosterScaled
                scale={0.65}
                primaryColor={posterMeta.primaryColor}
                feedbackUrl={scanUrl}
                topText={posterTop}
                bottomText={posterBottom}
                merchantName={posterMeta.businessName}
              />
            </div>

            <div className="w-full max-w-sm mt-6 space-y-3">
              {isMobile ? (
                <Link
                  to="/tack/$slug"
                  params={{ slug: `${DEMO_SLUG}-${DEMO_TOKEN}` }}
                  search={{ session: sessionId, lang: locale } as any}
                  className="block"
                >
                  <Button className="w-full gap-2" size="lg">
                    {t("demo.simulateScan")}
                  </Button>
                </Link>
              ) : (
                <a href={scanUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full gap-2" size="lg">
                    {t("demo.simulateScan")}
                  </Button>
                </a>
              )}
              <p className="text-xs text-muted-foreground text-center">
                {isMobile ? t("demo.helperMobile") : t("demo.helperDesktop")}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              {t("demo.liveDashboard")}
            </h3>

            <div className="grid grid-cols-3 gap-3">
              <Card className={`p-4 transition-colors duration-700 ${statFlash ? "bg-tack-green/25" : ""}`}>
                <p className="text-xs text-muted-foreground">{t("demo.todaysBurns")}</p>
                <p className="text-2xl font-display font-bold">{totalToday}</p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-muted-foreground">{t("demo.avgRating")}</p>
                <p className="text-2xl font-display font-bold">{avgRating}</p>
              </Card>
              <Card className={`p-4 transition-colors duration-700 ${statFlash ? "bg-tack-green/25" : ""}`}>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <TrendingUp size={12} /> {t("demo.estRoi")}
                </p>
                <p className="text-2xl font-display font-bold text-primary tabular-nums">
                  +{displayRoi} kr
                </p>
              </Card>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <Flame size={12} className="text-primary animate-pulse" /> {t("demo.currentlyBurning")}
              </p>
              {activeBurn ? (
                <div className="relative">
                  <div
                    className={`absolute -inset-0.5 rounded-xl pointer-events-none ${
                      activeBurn.isYou
                        ? "ring-2 ring-primary animate-pulse"
                        : "ring-2 ring-primary/40 animate-pulse"
                    }`}
                  />
                  <Card
                    className={`relative p-5 animate-in fade-in slide-in-from-top-2 duration-300 ${
                      activeBurn.isYou
                        ? "border-primary border-2 bg-tack-warm shadow-lg shadow-primary/20"
                        : ""
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-mono text-lg font-bold">{activeBurn.code}</p>
                          {activeBurn.isYou && (
                            <Badge className="text-[10px] h-5">{t("demo.thatsYours")}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                          <Phone size={12} />
                          <span className="truncate">{activeBurn.email}</span>
                        </div>
                      </div>
                      <Badge variant="secondary" className="gap-1 shrink-0">
                        <Flame size={12} className="text-primary animate-pulse" />
                        {mins}:{secs}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          size={12}
                          className={
                            i <= activeBurn.stars
                              ? "fill-primary text-primary"
                              : "text-muted-foreground/30"
                          }
                        />
                      ))}
                    </div>
                    {activeBurn.comment && (
                      <p className="text-sm italic text-muted-foreground mt-2 leading-snug">
                        "{activeBurn.comment}"
                      </p>
                    )}
                  </Card>
                </div>
              ) : (
                <Card className="p-6 text-center border-dashed">
                  <p className="text-sm text-muted-foreground">{t("demo.waitingNext")}</p>
                </Card>
              )}
            </div>

            <div>
              <p className="relative text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-tack-green" /> {t("demo.recentBurns")}
                <SparkleBurst burstKey={sparkleKey} />
              </p>
              <Card className="divide-y divide-border/50">
                {recentBurns.slice(0, 6).map((b, i) => (
                  <div
                    key={`${b.code}-${i}`}
                    className={`px-4 py-2.5 animate-in fade-in slide-in-from-top-1 duration-500 ${
                      b.isYou ? "bg-tack-warm" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <code className="text-xs font-bold tracking-widest">{b.code}</code>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: b.stars }).map((_, j) => (
                            <Star key={j} size={10} className="fill-tack-amber text-tack-amber" />
                          ))}
                        </div>
                      </div>
                      <span
                        className={`text-xs tabular-nums shrink-0 ml-2 ${
                          b.isYou ? "text-primary font-semibold" : "text-muted-foreground"
                        }`}
                      >
                        {b.agoLabel}
                      </span>
                    </div>
                    {b.comment && (
                      <p className="text-xs italic text-muted-foreground mt-1 leading-snug truncate">
                        "{b.comment}"
                      </p>
                    )}
                  </div>
                ))}
              </Card>
            </div>

            {feedback && feedback.status !== "redeemed" && (
              <Card className="p-4 bg-tack-warm border-primary/20">
                <p className="text-sm">
                  <span className="font-semibold">📬 {t("demo.emailSent")}</span>{" "}
                  {t("demo.emailSentBody", { contact: feedback.customer_email })}
                </p>
              </Card>
            )}

            <Card className="p-5 bg-secondary/30">
              <p className="text-sm font-medium mb-1">{t("demo.ctaTitle")}</p>
              <p className="text-xs text-muted-foreground mb-3">{t("demo.ctaBody")}</p>
              <Link to="/signup">
                <Button size="sm" className="w-full">
                  {t("demo.ctaButton")}
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
